import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Pencil, Check, X, Loader2, Percent, Euro, Copy, ToggleLeft, ToggleRight } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

import { API_URL } from '../../config/api';
const API = API_URL;

const AdminPromoCodes = () => {
  const [promoCodes, setPromoCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    code: '',
    discount_type: 'percent',
    discount_value: 10,
    min_order_amount: 0,
    max_uses: '',
    valid_until: '',
    applicable_to: 'all'
  });

  useEffect(() => {
    fetchPromoCodes();
  }, []);

  const fetchPromoCodes = async () => {
    try {
      const response = await fetch(`${API}/admin/promo-codes`, { credentials: 'include' });
      if (response.ok) {
        const data = await response.json();
        setPromoCodes(data.promo_codes || []);
      }
    } catch (error) {
      console.error('Error fetching promo codes:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const url = editingCode 
        ? `${API}/admin/promo-codes/${editingCode}`
        : `${API}/admin/promo-codes`;
      
      const method = editingCode ? 'PUT' : 'POST';
      
      const body = {
        ...formData,
        max_uses: formData.max_uses ? parseInt(formData.max_uses) : null,
        valid_until: formData.valid_until || null
      };
      
      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body)
      });
      
      if (response.ok) {
        console.log('Success:', editingCode ? 'Code promo mis à jour' : 'Code promo créé');
        fetchPromoCodes();
        resetForm();
      } else {
        const data = await response.json();
        console.log('Error:', data.detail || 'Erreur');
      }
    } catch (error) {
      console.log('Error:', 'Erreur de connexion');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Supprimer le code "${code}" ?`)) return;
    
    try {
      const response = await fetch(`${API}/admin/promo-codes/${code}`, {
        method: 'DELETE',
        credentials: 'include'
      });
      
      if (response.ok) {
        console.log('Success:', 'Code promo supprimé');
        fetchPromoCodes();
      }
    } catch (error) {
      console.log('Error:', 'Erreur');
    }
  };

  const handleToggleActive = async (code, currentStatus) => {
    try {
      const response = await fetch(`${API}/admin/promo-codes/${code}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ is_active: !currentStatus })
      });
      
      if (response.ok) {
        console.log('Success:', currentStatus ? 'Code désactivé' : 'Code activé');
        fetchPromoCodes();
      }
    } catch (error) {
      console.log('Error:', 'Erreur');
    }
  };

  const resetForm = () => {
    setFormData({
      code: '',
      discount_type: 'percent',
      discount_value: 10,
      min_order_amount: 0,
      max_uses: '',
      valid_until: '',
      applicable_to: 'all'
    });
    setEditingCode(null);
    setShowForm(false);
  };

  const startEdit = (promo) => {
    setFormData({
      code: promo.code,
      discount_type: promo.discount_type,
      discount_value: promo.discount_value,
      min_order_amount: promo.min_order_amount || 0,
      max_uses: promo.max_uses || '',
      valid_until: promo.valid_until ? promo.valid_until.split('T')[0] : '',
      applicable_to: promo.applicable_to || 'all'
    });
    setEditingCode(promo.code);
    setShowForm(true);
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    console.log('Success:', 'Code copié !');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-fgs-primary" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="font-heading text-xl font-bold text-white">Codes Promo</h2>
        <Button
          onClick={() => setShowForm(true)}
          className="bg-fgs-primary hover:bg-fgs-primary-hover"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouveau code
        </Button>
      </div>

      {/* Form */}
      {showForm && (
        <div className="bg-fgs-surface border border-fgs-border rounded-lg p-6 mb-6">
          <h3 className="text-white font-medium mb-4">
            {editingCode ? `Modifier "${editingCode}"` : 'Nouveau code promo'}
          </h3>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <div>
              <Label className="text-fgs-text-muted text-sm">Code</Label>
              <Input
                value={formData.code}
                onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                placeholder="PROMO20"
                className="bg-fgs-bg border-fgs-border text-white mt-1 uppercase"
                disabled={!!editingCode}
                required
              />
            </div>
            
            <div>
              <Label className="text-fgs-text-muted text-sm">Type de réduction</Label>
              <div className="flex gap-2 mt-1">
                <Button
                  type="button"
                  variant={formData.discount_type === 'percent' ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, discount_type: 'percent' })}
                  className={formData.discount_type === 'percent' 
                    ? 'bg-fgs-primary hover:bg-fgs-primary-hover' 
                    : 'border-fgs-border text-white hover:bg-fgs-surface-highlight'}
                >
                  <Percent className="w-4 h-4 mr-1" />
                  Pourcentage
                </Button>
                <Button
                  type="button"
                  variant={formData.discount_type === 'fixed' ? 'default' : 'outline'}
                  onClick={() => setFormData({ ...formData, discount_type: 'fixed' })}
                  className={formData.discount_type === 'fixed' 
                    ? 'bg-fgs-primary hover:bg-fgs-primary-hover' 
                    : 'border-fgs-border text-white hover:bg-fgs-surface-highlight'}
                >
                  <Euro className="w-4 h-4 mr-1" />
                  Montant fixe
                </Button>
              </div>
            </div>
            
            <div>
              <Label className="text-fgs-text-muted text-sm">
                Valeur {formData.discount_type === 'percent' ? '(%)' : '(€)'}
              </Label>
              <Input
                type="number"
                value={formData.discount_value}
                onChange={(e) => setFormData({ ...formData, discount_value: parseFloat(e.target.value) || 0 })}
                min="0"
                max={formData.discount_type === 'percent' ? 100 : undefined}
                className="bg-fgs-bg border-fgs-border text-white mt-1"
                required
              />
            </div>
            
            <div>
              <Label className="text-fgs-text-muted text-sm">Commande minimum (€)</Label>
              <Input
                type="number"
                value={formData.min_order_amount}
                onChange={(e) => setFormData({ ...formData, min_order_amount: parseFloat(e.target.value) || 0 })}
                min="0"
                className="bg-fgs-bg border-fgs-border text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-fgs-text-muted text-sm">Nombre max d'utilisations</Label>
              <Input
                type="number"
                value={formData.max_uses}
                onChange={(e) => setFormData({ ...formData, max_uses: e.target.value })}
                placeholder="Illimité"
                min="1"
                className="bg-fgs-bg border-fgs-border text-white mt-1"
              />
            </div>
            
            <div>
              <Label className="text-fgs-text-muted text-sm">Date d'expiration</Label>
              <Input
                type="date"
                value={formData.valid_until}
                onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
                className="bg-fgs-bg border-fgs-border text-white mt-1"
              />
            </div>
            
            <div className="md:col-span-2 lg:col-span-3 flex gap-3">
              <Button type="submit" disabled={saving} className="bg-fgs-primary hover:bg-fgs-primary-hover">
                {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                {editingCode ? 'Mettre à jour' : 'Créer'}
              </Button>
              <Button type="button" variant="outline" onClick={resetForm} className="border-fgs-border text-white">
                <X className="w-4 h-4 mr-2" />
                Annuler
              </Button>
            </div>
          </form>
        </div>
      )}

      {/* List */}
      {promoCodes.length === 0 ? (
        <div className="text-center py-12 bg-fgs-bg rounded-lg border border-fgs-border">
          <Percent className="w-12 h-12 text-fgs-text-muted mx-auto mb-4" />
          <p className="text-fgs-text-muted">Aucun code promo</p>
          <Button 
            onClick={() => setShowForm(true)} 
            className="mt-4 bg-fgs-primary hover:bg-fgs-primary-hover"
          >
            Créer votre premier code
          </Button>
        </div>
      ) : (
        <div className="bg-fgs-surface border border-fgs-border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-fgs-border bg-fgs-bg/50">
                <th className="px-4 py-3 text-left text-sm font-medium text-fgs-text-muted">Code</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-fgs-text-muted">Réduction</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-fgs-text-muted">Min. commande</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-fgs-text-muted">Utilisations</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-fgs-text-muted">Expiration</th>
                <th className="px-4 py-3 text-left text-sm font-medium text-fgs-text-muted">Statut</th>
                <th className="px-4 py-3 text-right text-sm font-medium text-fgs-text-muted">Actions</th>
              </tr>
            </thead>
            <tbody>
              {promoCodes.map((promo) => (
                <tr key={promo.code} className="border-b border-fgs-border/50 hover:bg-fgs-bg/30">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-white font-bold">{promo.code}</span>
                      <button 
                        onClick={() => copyCode(promo.code)}
                        className="p-1 hover:bg-fgs-surface-highlight rounded text-fgs-text-muted hover:text-white"
                      >
                        <Copy className="w-3 h-3" />
                      </button>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 rounded text-sm font-medium ${
                      promo.discount_value >= 50 ? 'bg-red-500/20 text-red-400' : 'bg-fgs-primary/20 text-fgs-primary'
                    }`}>
                      {promo.discount_type === 'percent' ? `-${promo.discount_value}%` : `-${promo.discount_value}€`}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-fgs-text-muted">
                    {promo.min_order_amount > 0 ? `${promo.min_order_amount}€` : '-'}
                  </td>
                  <td className="px-4 py-3 text-fgs-text-muted">
                    {promo.current_uses} / {promo.max_uses || '∞'}
                  </td>
                  <td className="px-4 py-3 text-fgs-text-muted text-sm">
                    {promo.valid_until 
                      ? new Date(promo.valid_until).toLocaleDateString('fr-FR')
                      : 'Illimité'}
                  </td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => handleToggleActive(promo.code, promo.is_active)}
                      className="flex items-center gap-2"
                    >
                      {promo.is_active ? (
                        <>
                          <ToggleRight className="w-6 h-6 text-green-500" />
                          <span className="text-green-400 text-sm">Actif</span>
                        </>
                      ) : (
                        <>
                          <ToggleLeft className="w-6 h-6 text-fgs-text-muted" />
                          <span className="text-fgs-text-muted text-sm">Inactif</span>
                        </>
                      )}
                    </button>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        onClick={() => startEdit(promo)}
                        className="p-2 hover:bg-fgs-surface-highlight rounded text-fgs-text-muted hover:text-white"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(promo.code)}
                        className="p-2 hover:bg-red-500/20 rounded text-fgs-text-muted hover:text-red-400"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminPromoCodes;
